import { Link, router } from '@inertiajs/react'
import { AdminShell } from '@/components/layout/AdminShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface AdminOrg {
  id: string
  name: string
  slug: string
  plan: string
  suspended: boolean
  suspended_at: string | null
  user_count: number
  app_count: number
  created_at: string
}

interface AdminUser {
  id: string
  email: string
  role: string
  suspended: boolean
  suspended_at: string | null
  created_at: string
  org_name: string | null
}

interface OrgApp {
  id: string
  name: string
  created_at: string
}

interface Props {
  admin: { email: string }
  organization: AdminOrg
  users: AdminUser[]
  applications: OrgApp[]
}

export default function Show({ admin, organization, users, applications }: Props) {
  function suspend() {
    router.post(`/admin/organizations/${organization.id}/suspend`)
  }

  function unsuspend() {
    router.post(`/admin/organizations/${organization.id}/unsuspend`)
  }

  function destroy() {
    if (confirm(`Delete organization "${organization.name}"? This cannot be undone.`)) {
      router.delete(`/admin/organizations/${organization.id}`)
    }
  }

  function impersonate(userId: string) {
    router.post('/admin/impersonations', { user_id: userId })
  }

  return (
    <AdminShell admin={admin}>
      <div className="space-y-8">
        <Link
          href="/admin/organizations"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Organizations
        </Link>

        <PageHeader
          eyebrow={`Organization · /${organization.slug}`}
          title={organization.name}
          actions={
            <div className="flex items-center gap-2">
              {organization.suspended ? (
                <Button variant="outline" onClick={unsuspend}>Unsuspend</Button>
              ) : (
                <Button variant="outline" onClick={suspend}>Suspend</Button>
              )}
              <Button variant="destructive" onClick={destroy}>Delete</Button>
            </div>
          }
        />

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Details
          </h2>
          <dl className="mt-4 divide-y divide-foreground/[0.06] text-sm">
            <div className="flex items-center justify-between py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Plan</dt>
              <dd className="capitalize">{organization.plan}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={organization.suspended ? 'destructive' : 'default'}>
                  {organization.suspended ? 'suspended' : 'active'}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Created</dt>
              <dd>{new Date(organization.created_at).toLocaleDateString()}</dd>
            </div>
          </dl>
        </section>

        <div>
          <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Members ({users.length})
          </h2>
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members.</p>
          ) : (
            <div className="surface-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">{user.role}</TableCell>
                      <TableCell>
                        <Badge variant={user.suspended ? 'destructive' : 'default'}>
                          {user.suspended ? 'suspended' : 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          {user.role !== 'super_admin' && (
                            <Button size="sm" variant="outline" onClick={() => impersonate(user.id)}>
                              Impersonate
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" asChild>
                            <Link href={`/admin/users/${user.id}`}>View</Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Applications ({applications.length})
          </h2>
          {applications.length === 0 ? (
            <p className="text-sm text-muted-foreground">No applications.</p>
          ) : (
            <div className="surface-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(app.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  )
}
